import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { makeReportKey, readReport, writeReport, readLastRun }
  from '../services/reportCache.js';

/**
 * useReportRestore — keeps a report on screen across navigation.
 *
 * Report pages unmount completely when you navigate away (V2 routing is param
 * dispatch, so there is no route-level state to survive in), and a re-run costs
 * ~3s for a day and 15s+ for a month. This owns the three mechanisms that make
 * coming back free, all of which are identical for every report:
 *
 *   1. The history stamp. What was actually run is written into the CURRENT
 *      history entry with `replace`, so browser Back lands on an entry that can
 *      re-seed itself. Replace, not push: Back must not grow an entry per run.
 *   2. The result cache (services/reportCache.js), keyed on report type +
 *      campus + config, with stale-while-revalidate so an edit made on a
 *      drill-down page shows on return without paying for the run again.
 *   3. The nav-menu offer. Arriving via the nav is a fresh intent — the history
 *      entry is brand new and carries nothing — so the last run is OFFERED
 *      rather than restored behind the user's back.
 *
 * WHAT STAYS IN THE PAGE. The shape of a report's config, how to seed page
 * state from one, and which setters a cached result feeds are all page
 * specific, so the hook never touches page state. It hands back configs and
 * cached payloads; the page applies them. That also keeps the hook out of the
 * TDZ trap you hit by passing it an `applyConfig` callback declared below it.
 *
 * Usage — call it above the state it describes but below `campusId`:
 *
 *   const restore = useReportRestore({ reportType: 'alerts', campusId, autoRun: run });
 *
 *   // state initialisers read the stamped entry
 *   const navState = restore.navState;
 *
 *   // in the run function
 *   const runConfig = { rangeId, filters, startDate, endDate };
 *   const { hit, revalidating } = restore.lookup(runConfig, { force });
 *   if (hit) { …apply hit.records / hit.summary…; if (!revalidating) return; }
 *   …fetch…
 *   if (failed) { if (revalidating) { restore.endRefresh(); return; } … }
 *   restore.commit(runConfig, { records, summary, overLimit, csvUrl });
 *
 *   // mount
 *   useEffect(() => { if (restore.consumeAutoRun()) runReport(); }, []);
 *
 * ONE CONTRACT TO GET RIGHT. `commit` stamps the config's own keys onto the
 * history entry, so a page's config keys and the keys its state initialisers
 * read off `navState` are the same namespace. Build a config of
 * { rangeId, filters, startDate, endDate } and the initialisers must read
 * navState.rangeId, navState.filters and so on. Diverge and the cache still
 * works while browser Back silently stops re-seeding — a failure with no error
 * anywhere. The upside of tying them together is that the cache key and the
 * history entry are provably the same description of the same run.
 *
 * See docs/REPORT_STATE_RESTORE_PLAN.md.
 *
 * @param {object}  opts
 * @param {string}  opts.reportType A stable cache scope, NOT the route param.
 *                                  Hardcode a literal: if a route is ever
 *                                  renamed, a key derived from the param would
 *                                  silently miss every cached report.
 * @param {*}       opts.campusId   Part of the cache key, so a cached report can
 *                                  never be served to a different community.
 * @param {boolean} [opts.autoRun]  The `/run` path segment. Arms the same
 *                                  one-shot that `state.autoRun` (Run Report
 *                                  Local) and a restored entry do — pass the
 *                                  prop only; the state keys are read here.
 */
export default function useReportRestore({ reportType, campusId, autoRun = false }) {
  const location = useLocation();
  const navigate = useNavigate();

  /* Frozen at mount. This is what the page's state initialisers seed from, and
     the hook rewrites location.state on every run (mechanism 1) — so a live read
     would let a later render show the initialisers their own output. */
  const arrivalRef = useRef(location.state);
  const navState = arrivalRef.current;

  /* A live mirror, used only for the passthrough in `commit`. Unlike navState
     this must NOT be frozen: a page that pushes its own state mid-mount would
     otherwise have it silently dropped by the next stamp. */
  const liveStateRef = useRef(location.state);
  liveStateRef.current = location.state;

  /* Three ways to say "run this on arrival", one ref so they cannot double fire:
       - the `autoRun` prop, from the /reports/<type>/run path segment;
       - `state.autoRun`, which is how ScheduledReportsPage's Run Report Local
         arrives at the six report routes that have no /run segment;
       - `state.restore`, a stamped history entry coming back via the browser.
     A restored entry deliberately has no /run segment: rewriting the URL to add
     one would leave the address bar permanently claiming "run". */
  const autoRunRef = useRef(
    Boolean(autoRun) || Boolean(navState?.autoRun) || Boolean(navState?.restore),
  );

  /* `ranAt` of the entry a run was served from, else null. Drives the "as of"
     note: a restored report is a claim about a past moment, and a Today range
     would otherwise quietly present old counts as current. */
  const [servedFrom, setServedFrom] = useState(null);
  /* A stale entry hydrates instantly and re-queries behind it; this is the
     indicator for that second half. */
  const [refreshing, setRefreshing] = useState(false);
  /** { key, config, ranAt, stale, count } or null. */
  const [offer, setOffer] = useState(null);

  /**
   * Consume the mount one-shot. Returns true at most once per mount, whether it
   * was armed by the /run segment or by a stamped history entry.
   */
  const consumeAutoRun = useCallback(() => {
    if (!autoRunRef.current) return false;
    autoRunRef.current = false;
    return true;
  }, []);

  /* The offer branch. Owned here rather than in the page's mount effect so a
     second report page gets it without writing anything: this effect is
     declared above the page's, so it still sees the un-consumed one-shot and
     can tell a fresh arrival from a Back return.

     Keyed on campusId, not mount-only. The campus selector swaps the community
     under a page that stays mounted, so an offer computed once at mount went
     on describing the PREVIOUS community's run — right count, right time,
     wrong community. Accepting it then replayed that community's config
     against this one: a hit if the ranges happened to line up, a surprise
     fresh run if they did not. */
  useEffect(() => {
    /* Nothing from the previous community survives on screen. The page clears
       its own records and status; these two are ours, and the "restored from"
       note in particular would otherwise sit above the new campus's empty
       state still quoting the old campus's run time. */
    setServedFrom(null);
    setRefreshing(false);

    if (autoRunRef.current) return;   // a stamped entry or /run — no question to ask
    const last = readLastRun(reportType, campusId);
    /* Invariant: re-deriving the key from the CURRENT campus and the offered
       config must reproduce the key it was stored under. campusId is part of
       makeReportKey, so a cross-campus offer cannot satisfy this.

       CLEAR on failure rather than return. Skipping leaves whatever was
       already on screen, which is the entire bug this dependency fixes — the
       guard was catching the mismatch and then doing nothing about it. */
    if (!last || makeReportKey(reportType, campusId, last.config) !== last.key) {
      setOffer(null);
      return;
    }
    /* The campus is stamped onto the offer so acceptOffer can refuse a stale
       one outright. */
    setOffer({ ...last, campusId });
  }, [reportType, campusId]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Take the offered config and clear the offer. Returns the config or null.
   *
   * The page seeds its own state from the result and then arms its deferred
   * run — it must not run synchronously, because setState is async and a run
   * started here would key the cache off the OLD config and miss.
   */
  const acceptOffer = useCallback(() => {
    const cfg = offer?.config;
    /* Campus is re-checked here as well as when the offer is built. Belt and
       braces, because this failure is SILENT: a config from another community
       simply misses the cache, and a miss runs a fresh report, so the button
       looks like it worked while quietly ignoring what it offered. Refusing is
       the honest outcome.

       String-compared to match makeReportKey's own normalisation. A raw !==
       would fail closed on a number-vs-string campusId, and failing closed
       here means a button that does nothing. */
    if (!cfg || String(offer.campusId) !== String(campusId)) return null;
    setOffer(null);
    return cfg;
  }, [offer, campusId]);

  const dismissOffer = useCallback(() => setOffer(null), []);

  /**
   * Cache check at the top of a run.
   *
   * @param {object} config          What is about to be run.
   * @param {object} [o]
   * @param {boolean} [o.force]      Skip the cache. The Run button passes this:
   *                                 a cache that ignored an explicit Run would
   *                                 be indistinguishable from a broken button.
   * @returns {{ hit: object|null, revalidating: boolean, key: string }}
   *          `hit` carries records / summary / overLimit / csvUrl / ranAt for
   *          the page to apply. `revalidating` means the hit was stale: render
   *          it and keep going, the fresh query runs underneath.
   */
  const lookup = useCallback((config, { force = false } = {}) => {
    const key = makeReportKey(reportType, campusId, config);
    const hit = force ? null : readReport(key);
    /* An offer cannot linger over real results — from here on this mount has
       run something, so the question has been answered either way. */
    setOffer(null);
    if (!hit) {
      setServedFrom(null);
      return { hit: null, revalidating: false, key };
    }
    setServedFrom(hit.ranAt);
    if (hit.stale) setRefreshing(true);
    return { hit, revalidating: Boolean(hit.stale), key };
  }, [reportType, campusId]);

  /**
   * A background refresh failed. Drops the indicator and leaves the stale
   * report on screen — blanking a report someone is reading, to punish them for
   * an edit made somewhere else, is the wrong trade.
   */
  const endRefresh = useCallback(() => setRefreshing(false), []);

  /**
   * A run succeeded: cache it, and stamp it into the history entry.
   *
   * Both use the same `config` object, so the cache key and the history entry
   * can never disagree about what was run.
   *
   * @param {object} config
   * @param {object} payload { records, summary, overLimit, csvUrl, extra }
   *        `extra` is an opaque bag for anything else the page renders from
   *        (Shift's hours + boundaries, Caregiver's totals, Hourly's stats).
   *        It comes back as `hit.extra`. Omit it if the page has none.
   */
  const commit = useCallback((config, { records, summary, overLimit, csvUrl, extra }) => {
    setRefreshing(false);
    setServedFrom(null);

    writeReport(makeReportKey(reportType, campusId, config), {
      /* Stored verbatim so the nav-menu offer replays exactly what ran. */
      config,
      records,
      summary,
      overLimit,
      csvUrl,
      extra,
    }, { reportType, campusId });

    /* Stamp the CURRENT history entry so Back lands on something that can
       re-seed itself.

       This also fixes a pre-existing wrong: the auto-run entry points
       (RunReportButton, the hourly drill-down, Run Now) pushed the config the
       user ARRIVED with, so changing the range and re-running left the entry
       stale and Back silently re-ran the original range.

       Everything else on the prior state is carried through rather than
       dropped. A deny-list, not an allow-list: each report page reads its own
       keys off this state (subType, sourceScheduleId, sourceDescription…) and a
       shared hook cannot know them all, so the safe default is to keep what it
       does not recognise.

       The exceptions are the keys that describe a ONE-OFF INTENT rather than
       context, and so must not be re-presented to a later mount:
         - `autoRun`, which seven report pages read to arm exactly the one-shot
           this hook now owns. Leaving it on would make every restored entry
           look like a fresh Run Report Local.
         - `hour`, the hourly drill-down's clamp key, which is meaningless once
           `restore` exempts the clamp.

       Dates go as ISO strings to match what the seeding code expects. */
    const { restore: _wasRestore, autoRun: _ar, hour: _h, rangeId: _rid,
      filters: _f, startDate: _sd, endDate: _ed,
      ...passthrough } = liveStateRef.current || {};
    navigate(location.pathname, {
      replace: true,
      state: { ...passthrough, restore: true, ...config },
    });
  }, [reportType, campusId, navigate, location.pathname]);

  return {
    navState,
    consumeAutoRun,
    lookup,
    commit,
    endRefresh,
    servedFrom,
    refreshing,
    offer,
    acceptOffer,
    dismissOffer,
  };
}
